import { colors, useTheme } from '@pixee/design-system'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { materialDark, materialLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import remarkGfm from 'remark-gfm'
import { ErrorBoundary } from './error-boundaries/error-boundary'
import { MermaidDiagram } from './mermaid-diagram.tsx'
import * as styles from './rendered-markdown.css'

type RenderedMarkdownProps = {
  markdown: string | undefined | null
  /**
   * Overrides how ```mermaid fences render. Defaults to a plain `MermaidDiagram`; callers with a
   * large diagram can supply their own wrapper (e.g. a zoomable viewport).
   */
  renderMermaid?: (code: string) => React.ReactNode
}

const extractLanguage = (className?: string): string => className?.match(/language-(\S+)/)?.[1] ?? 'text'

export const RenderedMarkdown: React.FC<RenderedMarkdownProps> = ({ markdown, renderMermaid }) => {
  return (
    <div className={styles.description}>
      <ErrorBoundary fallbackTitle="Failed to render content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ children, ...props }) => (
              <a className={styles.markdownLink} target="_blank" rel="noreferrer" {...props}>
                {children}
              </a>
            ),
            code: ({ className, ...props }) => <InlineCode language={extractLanguage(className)} {...props} />,
            pre: ({ children, ...props }) => {
              const childProps = (children as { props?: { className?: string; children?: string } })?.props
              const language = extractLanguage(childProps?.className)
              if (language === 'mermaid') {
                const code = childProps?.children ?? ''
                return (
                  <ErrorBoundary
                    fallback={
                      <CodeBlock language={language} showLineNumbers={false} {...props}>
                        {children}
                      </CodeBlock>
                    }
                  >
                    {renderMermaid ? renderMermaid(code) : <MermaidDiagram code={code} />}
                  </ErrorBoundary>
                )
              }
              return (
                <CodeBlock language={language} showLineNumbers={false} {...props}>
                  {children}
                </CodeBlock>
              )
            },
            h1: ({ children, ...props }) => (
              <h1 className={styles.markdownHeadingH1} {...props}>
                {children}
              </h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 className={styles.markdownHeadingH2} {...props}>
                {children}
              </h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 className={styles.markdownHeadingH3} {...props}>
                {children}
              </h3>
            ),
            h4: ({ children, ...props }) => (
              <h4 className={styles.markdownHeadingH4} {...props}>
                {children}
              </h4>
            ),
            h5: ({ children, ...props }) => (
              <h5 className={styles.markdownHeadingH5} {...props}>
                {children}
              </h5>
            ),
            h6: ({ children, ...props }) => (
              <h6 className={styles.markdownHeadingH6} {...props}>
                {children}
              </h6>
            ),
          }}
        >
          {markdown}
        </ReactMarkdown>
      </ErrorBoundary>
    </div>
  )
}

const InlineCode = ({ language, children, ...props }: React.HTMLProps<HTMLElement> & { language: string }) => {
  const { theme } = useTheme()

  return (
    <SyntaxHighlighter
      language={language}
      style={(theme === 'dark' ? materialDark : materialLight) as unknown}
      PreTag="code"
      CodeTag="code"
      customStyle={{
        margin: '0',
        padding: '4px 8px',
        borderRadius: '8px',
        backgroundColor: theme === 'dark' ? colors.neutral.orbit : colors.neutral.neutral10,
        width: '100%',
      }}
      codeTagProps={{
        style: {
          fontFamily: "'Inconsolata', monospace",
          fontSize: '1rem',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '24px',
          color: theme === 'dark' ? colors.neutral.neutral10 : colors.neutral.orbit,
        },
      }}
      {...props}
    >
      {children}
    </SyntaxHighlighter>
  )
}

const CodeBlock = ({
  language,
  showLineNumbers,
  children,
  ...props
}: React.HTMLProps<HTMLElement> & { language: string; showLineNumbers: boolean }) => {
  const { theme } = useTheme()

  // TODO: Is this really safe?
  const typedChildren = children as { props?: { children?: string } } | undefined
  const code = typedChildren?.props?.children
  const codeLines = code?.split('\n')

  const addedLines = codeLines?.reduce(
    (acc: number[], line: string, index: number) => (line.startsWith('+') ? [index + 1, ...acc] : acc),
    []
  )
  const removedLines = codeLines?.reduce(
    (acc: number[], line: string, index: number) => (line.startsWith('-') ? [index + 1, ...acc] : acc),
    []
  )

  return (
    <SyntaxHighlighter
      language={language}
      style={(theme === 'dark' ? materialDark : materialLight) as unknown}
      customStyle={{
        borderRadius: '8px',
        backgroundColor: theme === 'dark' ? colors.neutral.orbit : colors.neutral.neutral10,
        color: theme === 'dark' ? colors.neutral.neutral10 : colors.neutral.orbit,
      }}
      codeTagProps={{
        style: {
          fontFamily: "'Inconsolata', monospace",
          fontSize: '1rem',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '24px',
        },
      }}
      wrapLines={true}
      wrapLongLines={true}
      showLineNumbers={showLineNumbers}
      lineProps={(lineNumber: number) => {
        if (removedLines?.includes(lineNumber)) {
          return {
            style: {
              backgroundColor: theme === 'dark' ? '#4B1818' : '#FFDCE0',
            },
          } as React.HTMLAttributes<HTMLElement>
        }
        if (addedLines?.includes(lineNumber)) {
          return {
            style: {
              backgroundColor: theme === 'dark' ? '#2D4F2D' : ' #E0F2E9',
            },
          } as React.HTMLAttributes<HTMLElement>
        }
        return {} as React.HTMLAttributes<HTMLElement>
      }}
      {...props}
    >
      {code}
    </SyntaxHighlighter>
  )
}
